<template>
  <div class="rt-editor" :class="{ 'rt-editor--readonly': readonly }">
    <editor-content v-if="editor" class="rt-content" :editor="editor"/>
  </div>
</template>

<script>
import { Editor, EditorContent } from 'tiptap'
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Placeholder,
  Table,
  TableHeader,
  TableCell,
  TableRow,
} from 'tiptap-extensions'

export default {
  name: 'RichTextEditor',
  components: { EditorContent },
  props: {
    value: { type: String, default: '' },
    json: { type: [Object, Array, String], default: null },
    readonly: { type: Boolean, default: false },
    placeholder: { type: String, default: 'Start writing your document…' }
  },
  data () {
    return {
      editor: null,
      lastEmittedHtml: ''
    }
  },
  watch: {
    value (newVal) {
      if (!this.editor) return
      if (newVal !== this.lastEmittedHtml) {
        this.editor.setContent(newVal || '', false)
      }
    },
    readonly (val) {
      if (this.editor) {
        this.editor.setOptions({ editable: !val })
      }
    }
  },
  mounted () {
    this.editor = new Editor({
      editable: !this.readonly,
      extensions: [
        new Blockquote(),
        new BulletList(),
        new CodeBlock(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3] }),
        new HorizontalRule(),
        new ListItem(),
        new OrderedList(),
        new TodoItem(),
        new TodoList(),
        new Link({ openOnClick: false }),
        new Bold(),
        new Code(),
        new Italic(),
        new Strike(),
        new Underline(),
        new History(),
        new Table({ resizable: true }),
        new TableHeader(),
        new TableCell(),
        new TableRow(),
        new Placeholder({
          emptyEditorClass: 'is-editor-empty',
          emptyNodeClass: 'is-empty',
          emptyNodeText: this.placeholder,
          showOnlyWhenEditable: true,
          showOnlyCurrent: false
        })
      ],
      content: this.parseInitialContent(),
      onUpdate: ({ getHTML, getJSON }) => {
        const html = getHTML()
        this.lastEmittedHtml = html
        this.$emit('input', html)
        this.$emit('update:json', getJSON())
      }
    })
    // Notify parents that the underlying TipTap Editor is ready so a
    // sibling RichTextToolbar component can bind to it. This replaces
    // the older "render the toolbar inline" architecture which caused
    // duplicate toolbars when DOM was relocated manually.
    this.$emit('editor-ready', this.editor)
  },
  beforeDestroy () {
    if (this.editor) {
      this.$emit('editor-ready', null)
      this.editor.destroy()
      this.editor = null
    }
  },
  methods: {
    parseInitialContent () {
      // Prefer the structured JSON when both are provided so the
      // document re-hydrates with the exact node types the editor saw
      // when it last saved. Fall back to HTML which TipTap parses
      // into a (possibly lossy) ProseMirror tree.
      if (this.json) {
        if (typeof this.json === 'string') {
          try { return JSON.parse(this.json) } catch (e) { /* ignore */ }
        } else if (this.json && typeof this.json === 'object') {
          return this.json
        }
      }
      return this.value || ''
    },
    getJSON () {
      return this.editor ? this.editor.getJSON() : null
    },
    getHTML () {
      return this.editor ? this.editor.getHTML() : ''
    }
  }
}
</script>

<style scoped>
.rt-editor {
  display: flex;
  flex-direction: column;
}

.rt-content {
  padding: 0;
  overflow: visible;
}

/* TipTap ProseMirror content styling — needs >>> since the rendered
   editor lives outside Vue's scoped style boundary. */
.rt-content >>> .ProseMirror {
  outline: none;
  font-size: 0.95rem;
  line-height: 1.7;
  color: #1f2a44;
  min-height: 380px;
}
.rt-editor--readonly .rt-content >>> .ProseMirror { min-height: 0; }

.rt-content >>> .ProseMirror > * + * { margin-top: 0.85rem; }
.rt-content >>> .ProseMirror h1 { font-size: 1.6rem; font-weight: 700; color: #0f172a; line-height: 1.25; margin-top: 1.4rem; }
.rt-content >>> .ProseMirror h2 { font-size: 1.3rem; font-weight: 700; color: #0f172a; line-height: 1.3; margin-top: 1.25rem; }
.rt-content >>> .ProseMirror h3 { font-size: 1.1rem; font-weight: 700; color: #1f2a44; line-height: 1.35; margin-top: 1rem; }
.rt-content >>> .ProseMirror p { margin: 0; }
.rt-content >>> .ProseMirror ul, .rt-content >>> .ProseMirror ol { padding-left: 1.4rem; }
.rt-content >>> .ProseMirror ul li, .rt-content >>> .ProseMirror ol li { margin: 0.2rem 0; }
.rt-content >>> .ProseMirror blockquote {
  border-left: 3px solid #16a07a;
  background: #f5fbf8;
  padding: 0.6rem 0.85rem;
  margin: 0;
  color: #134e3a;
  border-radius: 0 8px 8px 0;
}
.rt-content >>> .ProseMirror code {
  background: #f1f5f9;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  font-size: 0.85em;
  color: #b91c1c;
}
.rt-content >>> .ProseMirror pre {
  background: #0f172a;
  color: #e2e8f0;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 0.85rem;
  line-height: 1.55;
}
.rt-content >>> .ProseMirror pre code {
  background: transparent;
  color: inherit;
  padding: 0;
  font-size: inherit;
}
.rt-content >>> .ProseMirror a {
  color: #0e5b46;
  text-decoration: underline;
  text-decoration-color: rgba(22, 160, 122, 0.45);
  text-underline-offset: 2px;
}
.rt-content >>> .ProseMirror a:hover { color: #16a07a; text-decoration-color: #16a07a; }
.rt-content >>> .ProseMirror hr {
  border: 0;
  border-top: 1px dashed #cbd5e1;
  margin: 1.25rem 0;
}
.rt-content >>> .ProseMirror ul[data-type="todo_list"] { padding-left: 0; list-style: none; }
.rt-content >>> .ProseMirror li[data-type="todo_item"] { display: flex; gap: 0.5rem; }

/* Tables — used both in edit mode and in the reader. */
.rt-content >>> .ProseMirror .tableWrapper {
  overflow-x: auto;
  margin: 0.85rem 0;
  border-radius: 10px;
  border: 1px solid #e5e9f2;
}

.rt-content >>> .ProseMirror table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0;
  background: #ffffff;
  font-size: 0.88rem;
  overflow: hidden;
}

.rt-content >>> .ProseMirror table td,
.rt-content >>> .ProseMirror table th {
  border: 1px solid #e5e9f2;
  padding: 0.55rem 0.7rem;
  vertical-align: top;
  min-width: 80px;
  position: relative;
  word-break: break-word;
}

.rt-content >>> .ProseMirror table th {
  background: linear-gradient(180deg, #f0faf5 0%, #e6f4ee 100%);
  color: #0e5b46;
  font-weight: 700;
  text-align: left;
}

.rt-content >>> .ProseMirror table tr:nth-child(even) td { background: #fbfcfe; }

.rt-content >>> .ProseMirror table .selectedCell { background: rgba(22, 160, 122, 0.12); }
.rt-content >>> .ProseMirror table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #16a07a;
  cursor: col-resize;
  z-index: 1;
}

.rt-content >>> .ProseMirror.resize-cursor { cursor: col-resize; }

.rt-editor--readonly .rt-content >>> .ProseMirror .column-resize-handle { display: none; }

/* Placeholder visible when the doc is empty. */
.rt-content >>> .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-empty-text);
  float: left;
  color: #94a3b8;
  pointer-events: none;
  height: 0;
}
</style>
